import React from 'react';
import { useTranslation } from 'react-i18next';

import type { CreditPack } from '../../config/creditPacks';
import { CREDIT_PACKS } from '../../config/creditPacks';
import { usePlanStatus } from '../../hooks/usePlanStatus';
import { PlanIcon } from '../../utils/planDisplay';
import {
  formatLocalizedPlanTimeline,
  localizedPackName,
  localizedPackPeriodDisplay,
} from './creditCheckoutUtils';
import {
  getPackTierIndex,
  isHigherTierPack,
  isSamePack,
} from '../../utils/planUpgrade';

interface CheckoutPlanSwitcherProps {
  selectedPackId: string;
  onSelectPlan: (packId: string) => void;
  formatSwitcherPlanPrice: (plan: CreditPack) => string;
}

const CheckoutPlanSwitcher: React.FC<CheckoutPlanSwitcherProps> = ({
  selectedPackId,
  onSelectPlan,
  formatSwitcherPlanPrice,
}) => {
  const { t, i18n } = useTranslation();
  const planStatus = usePlanStatus();
  const currentPackId = planStatus.isActive ? planStatus.planId : null;
  const timeline = formatLocalizedPlanTimeline(
    planStatus.periodEnd,
    planStatus.daysLeft,
    planStatus.isExpired,
    t,
    i18n.language,
  );

  const currentPlanLabel =
    currentPackId && ['pack-monthly', 'pack-quarterly', 'pack-halfyearly'].includes(currentPackId)
      ? localizedPackName(currentPackId, t)
      : planStatus.planDisplayName;

  return (
    <div className="credit-checkout-plan-panel">
      {currentPackId ? (
        <div className="credit-checkout-current-plan">
          <p className="credit-checkout-current-plan-label">
            {t('creditsPurchaseModal.currentPlanLabel', 'Your current plan')}
          </p>
          <div className="credit-checkout-current-plan-row">
            <PlanIcon
              planId={currentPackId}
              planKey={planStatus.planKey}
              size={16}
            />
            <div className="credit-checkout-current-plan-copy">
              <strong>{currentPlanLabel}</strong>
              {timeline && <span>{timeline}</span>}
            </div>
          </div>
        </div>
      ) : (
        <div className="credit-checkout-current-plan credit-checkout-current-plan--free">
          <p className="credit-checkout-current-plan-label">
            {t('creditsPurchaseModal.currentPlanLabel', 'Your current plan')}
          </p>
          <div className="credit-checkout-current-plan-row">
            <PlanIcon planId={null} planKey="free" size={16} />
            <div className="credit-checkout-current-plan-copy">
              <strong>{t('views.pricing.planFree', 'Free')}</strong>
              <span>
                {t(
                  'creditsPurchaseModal.freePlanAccessHint',
                  'Limited access · upgrade for full features',
                )}
              </span>
            </div>
          </div>
        </div>
      )}

      <p className="credit-checkout-plan-switch-label">
        {t('creditsPurchaseModal.choosePlanLabel', 'Choose a plan')}
      </p>
      <div
        className="credit-checkout-plan-switch-grid"
        role="listbox"
        aria-label={t('creditsPurchaseModal.plansListAria', 'Subscription plan')}
      >
        {CREDIT_PACKS.map((pack) => {
          const isCurrent = isSamePack(pack.id, currentPackId);
          const isUpgrade = isHigherTierPack(pack.id, currentPackId);
          const isDowngrade = currentPackId
            ? getPackTierIndex(pack.id) < getPackTierIndex(currentPackId)
            : false;
          const isSelected = pack.id === selectedPackId;
          const periodLabel = localizedPackPeriodDisplay(pack.id, t);

          return (
            <button
              key={pack.id}
              type="button"
              role="option"
              aria-selected={isSelected}
              disabled={isDowngrade}
              className={[
                'credit-checkout-plan-option',
                isSelected ? 'credit-checkout-plan-option--selected' : '',
                isCurrent ? 'credit-checkout-plan-option--current' : '',
                isDowngrade ? 'credit-checkout-plan-option--disabled' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => onSelectPlan(pack.id)}
            >
              <div className="credit-checkout-plan-option-top">
                <PlanIcon
                  planId={pack.id}
                  planKey={
                    pack.id === 'pack-monthly'
                      ? 'monthly'
                      : pack.id === 'pack-quarterly'
                        ? 'quarterly'
                        : 'halfyearly'
                  }
                  size={15}
                />
                <span className="credit-checkout-plan-option-name">
                  {localizedPackName(pack.id, t)}
                </span>
                {isCurrent && (
                  <span className="credit-checkout-plan-option-badge">
                    {t('creditsPurchaseModal.badgeCurrent', 'Current')}
                  </span>
                )}
                {!isCurrent && isUpgrade && (
                  <span className="credit-checkout-plan-option-badge credit-checkout-plan-option-badge--upgrade">
                    {t('creditsPurchaseModal.badgeUpgrade', 'Upgrade')}
                  </span>
                )}
              </div>
              <span className="credit-checkout-plan-option-price">
                {formatSwitcherPlanPrice(pack)}
                {periodLabel && <span> / {periodLabel}</span>}
              </span>
            </button>
          );
        })}
      </div>
      {currentPackId && isHigherTierPack(selectedPackId, currentPackId) && (
        <p className="credit-checkout-upgrade-note">
          {t(
            'creditsPurchaseModal.upgradeCycleNote',
            'Upgrading starts a new billing cycle at the selected rate. Cancel your current plan under Account → Plan if you want to avoid overlapping subscriptions.',
          )}
        </p>
      )}
    </div>
  );
};

export default CheckoutPlanSwitcher;
